import { useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../../../components/buttons/button";
import { SearchInput } from "../../../components/inputs/searchInput";
import { BareLayout } from "../../../components/layout/bareLayout";
import { Protected } from "../../../components/layout/protected";
import { QRScanner } from "../../../components/modal/app_modals/qrScannerModal";
import { GuestsTable } from "../../../components/table/app_tables/guestsTable";
import { TabNav } from "../../../components/tabNav";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useEventPermissions } from "../../../customeHooks/useEventPermissions";
import { useModalControl } from "../../../customeHooks/useModalControl";
import {
  useGetEventGuestsQuery,
  useGetEventQuery,
} from "../../../redux/features/event/event.api";
import { GuestStatus } from "../../../redux/features/event/event.types";
import { EventPermissions, QueryParams } from "../../../utils/types";

export const EventGuestsPage = () => {
  const { id } = useParams<{ id: string }>();

  const [qrId, setQrId] = useState<string>();
  const [userEventPermissions, setUserEventPermissions] = useState<
    EventPermissions[]
  >([]);

  const [queryParams, setQueryParams] = useState<
    QueryParams & {
      search: string;
      sort: "asc" | "desc";
      status?: GuestStatus;
    }
  >({
    search: "",
    sort: "desc",
    status: undefined,
    page: 1,
    limit: 10,
  });

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const { data: guests, isFetching: fetchingGuests } = useGetEventGuestsQuery(
    {
      id: id!,
      search: queryParams.search,
      status: queryParams.status,
      page: queryParams.page,
      limit: queryParams.limit,
    },
    { refetchOnMountOrArgChange: true },
  );

  const { data: eventResponse, isFetching: fetchingEvent } = useGetEventQuery(
    {
      id: id,
    },
    { refetchOnMountOrArgChange: true },
  );

  useEventPermissions({
    event: eventResponse?.data,
    setPermissions: setUserEventPermissions,
  });

  return (
    <Protected>
      <BareLayout
        title={`${eventResponse?.data.name} Check In`}
        bodyClassName="flex flex-col gap-[2px]"
        loading={fetchingEvent}
      >
        <BgWrapper className="!rounded-b-none">
          <TabNav
            navs={[
              {
                title: "All Guests",
                to: "all",
                onClick: () => {
                  setQueryParams({
                    ...queryParams,
                    status: undefined,
                    page: 1,
                  });
                },
              },
              {
                title: "Going",
                to: "unchecked-in",
                onClick: () => {
                  setQueryParams({
                    ...queryParams,
                    status: "unchecked-in",
                    page: 1,
                  });
                },
              },
              {
                title: "Checked In",
                to: "checked-in",
                onClick: () => {
                  setQueryParams({
                    ...queryParams,
                    status: "checked-in",
                    page: 1,
                  });
                },
              },
            ]}
            activeTab={
              queryParams.status === undefined ? "all" : queryParams.status
            }
            variant="switcher"
            className=""
            tabClassName=""
          />
        </BgWrapper>

        <BgWrapper className="flex flex-col gap-3 !rounded-t-none">
          <div className="flex w-full flex-col items-center justify-between gap-4 md:flex-row">
            <SearchInput
              searchFunction={(value) => {
                setQueryParams({
                  ...queryParams,
                  search: value,
                  page: 1,
                });
              }}
              placeholder="Search"
              className="md:!max-w-[300px]"
            />

            <div className="grid w-full grid-cols-2 items-center gap-3 md:flex md:w-fit md:flex-row">
              <Button
                type="button"
                onClick={() => {
                  handleModalOpen("qrScanner");
                }}
                text="Scan"
                className="!min-w-fit"
                icon={"camera"}
                variation="plain-grey"
              />
            </div>
          </div>

          <GuestsTable
            guests={guests?.data?.data ?? []}
            loading={fetchingGuests}
            showTimeAgo={true}
            isPaginated
            currentPage={queryParams.page}
            totalCount={guests?.data?.totalCount ?? 0}
            itemsPerPage={queryParams.limit}
            onPageChange={(page) => {
              setQueryParams({
                ...queryParams,
                page: page,
              });
            }}
            qrScanId={qrId}
            setQrScanId={setQrId}
            eventCurrency={"NGN"}
            userEventPermissions={userEventPermissions}
          />
        </BgWrapper>
      </BareLayout>

      <QRScanner
        open={modal === "qrScanner" && modalOpen}
        handleClose={() => {
          handleModalClose();
        }}
        onSuccess={(result) => {
          handleModalClose();
          setQrId(result);
        }}
      />
    </Protected>
  );
};
